/** @file mesh.rebindMesh: re-captures a skinned mesh's bind at the current setup pose (§6.7). */
import { z } from 'zod';
import type { Id } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';
import { bindMatrix } from './bindMatrix';
import { meshDraft } from './meshDraft';
import { sameNumbers } from './sameNumbers';
import { setupWorldOf } from './setupWorldOf';
import { skinBlend } from './skinBlend';
import { skinnedDraft } from './skinnedDraft';
import { transformDeformKeys } from './transformDeformKeys';
import { transformMeshPoints } from './transformMeshPoints';
import { weightedBones } from './weightedBones';

/** mesh.rebindMesh: v := M_v·v, B := setup worlds of exactly the weighted bones, deform δ := Lin(M_v)·δ; weights unchanged. */
export const command = defineCommand({
  name: 'mesh.rebindMesh',
  description: "Re-capture a skinned mesh's bind at the current setup pose: bind vertices become the setup skinned positions and each weighted bone's bind pose its setup world (weights unchanged, deform keys converted, the setup look preserved).",
  input: z.object({ target: z.string().min(1) }).strict(),
  apply(draft, input, ctx) {
    const { id, mesh } = meshDraft(draft, ctx, input.target);
    const g = skinnedDraft(mesh);
    const worldOf = setupWorldOf(draft);
    const bones = weightedBones(g.weights);
    const bindPose: Record<Id, number[]> = Object.fromEntries(bones.map((bone) => [bone, bindMatrix(worldOf, bone, 'target')]));
    if (bones.length === Object.keys(g.bindPose).length && bones.every((bone) => sameNumbers(g.bindPose[bone], bindPose[bone]))) return;
    const mats = skinBlend(g, worldOf, `attachments.${id}.geometry`);
    g.bindVertices = transformMeshPoints(g.bindVertices, mats, 0, false);
    g.bindPose = bindPose;
    transformDeformKeys(draft, id, mats);
    draft.meta.updatedAt = ctx.now;
  },
});
